import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCartStore } from '../stores/cartStore';
import { useAuthStore } from '../stores/authStore';
import { usePrice } from '../hooks/usePrice';

export default function Cart() {
  const { formatPrice } = usePrice();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { items, total, itemCount, isLoading, fetchCart, loadGuestCart, updateItem, removeItem } = useCartStore();

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    } else {
      loadGuestCart();
    }
  }, [isAuthenticated, fetchCart, loadGuestCart]);

  const handleQuantityChange = async (itemId: string, newQuantity: number) => {
    if (newQuantity < 1) return;
    try {
      await updateItem(itemId, newQuantity, isAuthenticated);
    } catch (error: any) {
      toast.error(error.message || 'Failed to update quantity');
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    try {
      await removeItem(itemId, isAuthenticated);
      toast.success('Item removed from cart');
    } catch (error: any) {
      toast.error(error.message || 'Failed to remove item');
    }
  };

  const handleCheckout = () => {
    if (items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    if (!isAuthenticated) {
      toast('Please login to checkout', { icon: '🔒' });
      navigate('/login', { state: { from: '/checkout' } });
      return;
    }
    navigate('/checkout');
  };

  if (isLoading) {
    return (
      <div className="py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="font-western text-4xl text-cream mb-8">Your Cart</h1>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="card p-6 animate-pulse">
                <div className="flex gap-4">
                  <div className="w-24 h-24 bg-leather rounded" />
                  <div className="flex-1">
                    <div className="h-6 bg-leather rounded w-1/3 mb-2" />
                    <div className="h-4 bg-leather rounded w-1/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-western text-4xl text-cream mb-6">Your Cart</h1>
          <p className="text-text-secondary mb-8">Your cart is empty.</p>
          <Link to="/products" className="btn-primary">
            Browse Products
          </Link>
        </div>
      </div>
    );
  }

  // Group items by seller
  const itemsBySeller: Record<string, { seller: typeof items[0]['seller']; items: typeof items }> = {};
  for (const item of items) {
    const sellerId = item.seller.id;
    if (!itemsBySeller[sellerId]) {
      itemsBySeller[sellerId] = {
        seller: item.seller,
        items: [],
      };
    }
    itemsBySeller[sellerId].items.push(item);
  }

  return (
    <div className="py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-western text-4xl text-cream mb-8">Your Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {Object.values(itemsBySeller).map(({ seller, items: sellerItems }) => (
              <div key={seller.id} className="card overflow-hidden">
                {/* Seller Header */}
                <div className="bg-leather px-6 py-3 border-b border-gold/10">
                  <Link
                    to={`/sellers/${seller.storeSlug}`}
                    className="text-text-secondary hover:text-cream transition-colors"
                  >
                    Sold by <span className="text-cream">{seller.storeName}</span>
                  </Link>
                </div>

                {/* Items */}
                <div className="divide-y divide-gold/10">
                  {sellerItems.map(item => (
                    <div key={item.id} className="p-6">
                      <div className="flex gap-4">
                        {/* Thumbnail */}
                        <Link
                          to={`/products/${item.product.id}`}
                          className="w-24 h-24 bg-leather rounded overflow-hidden flex-shrink-0"
                        >
                          {item.product.thumbnailUrl ? (
                            <img
                              src={item.product.thumbnailUrl}
                              alt={item.product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="text-gold/30 font-western text-2xl">OO</span>
                            </div>
                          )}
                        </Link>

                        {/* Details */}
                        <div className="flex-1 min-w-0">
                          <Link
                            to={`/products/${item.product.id}`}
                            className="text-cream font-semibold hover:text-gold transition-colors line-clamp-2"
                          >
                            {item.product.name}
                          </Link>

                          <div className="mt-2 text-gold font-bold">
                            {formatPrice(item.product.price)}
                          </div>

                          {/* Quantity Controls */}
                          <div className="mt-3 flex items-center gap-4">
                            <div className="flex items-center border border-gold/20 rounded">
                              <button
                                onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                                disabled={item.quantity <= 1}
                                className="px-3 py-1 text-text-secondary hover:text-cream disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                -
                              </button>
                              <span className="px-4 py-1 text-cream border-x border-gold/20">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                                className="px-3 py-1 text-text-secondary hover:text-cream"
                              >
                                +
                              </button>
                            </div>

                            <button
                              onClick={() => handleRemoveItem(item.id)}
                              className="text-text-muted hover:text-error transition-colors text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        </div>

                        {/* Subtotal */}
                        <div className="text-right">
                          <div className="text-cream font-bold">
                            {formatPrice(item.product.price * item.quantity)}
                          </div>
                          {item.quantity > 1 && (
                            <div className="text-text-muted text-sm">
                              {item.quantity} x {formatPrice(item.product.price)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="card p-6 sticky top-8">
              <h2 className="font-semibold text-cream mb-4">Order Summary</h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-text-secondary">
                  <span>Items ({itemCount})</span>
                  <span>{formatPrice(total)}</span>
                </div>
                <div className="flex justify-between text-text-secondary">
                  <span>Platform Fee</span>
                  <span className="text-success">Included</span>
                </div>
              </div>

              <div className="border-t border-gold/10 pt-4 mb-6">
                <div className="flex justify-between text-lg">
                  <span className="text-cream font-semibold">Total</span>
                  <span className="text-gold font-bold">{formatPrice(total)}</span>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                className="btn-primary w-full py-4"
              >
                {isAuthenticated ? 'Proceed to Checkout' : 'Login to Checkout'}
              </button>

              {!isAuthenticated && (
                <p className="text-text-muted text-sm text-center mt-3">
                  Your cart will be saved when you login
                </p>
              )}

              <Link
                to="/products"
                className="block text-center mt-4 text-text-secondary hover:text-cream transition-colors text-sm"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
